import I18n from 'I18n';
import { useMemo } from 'react';
import { useFilterConfig } from '../filters/hooks/useFilterConfig';
/**
 * Hook that formats listing page titles for SEO according to the requirements:
 *
 * Breeze Agents: {Name} - AI {Team} Agent for HubSpot | {Provider}
 * Apps: {Name} - {Category} App For HubSpot | {Provider}
 */
export function useListingDocumentTitle(props) {
  const {
    AgentTeam,
    Category
  } = useFilterConfig();
  return useMemo(() => {
    if (!(props !== null && props !== void 0 && props.name) || !(props !== null && props !== void 0 && props.productType)) {
      return null;
    }

    // Handle categories - could be array or single string
    let normalizedCategories;
    if (props.category && Array.isArray(props.category)) {
      normalizedCategories = props.category;
    }
    if (props.productType === 'AGENT') {
      return formatAgentTitle(props.name, props.agentTeam, props.companyName, AgentTeam);
    } else {
      return formatAppTitle(props.name, normalizedCategories, props.companyName, Category);
    }
  }, [props, AgentTeam, Category]);
}
function formatAgentTitle(name, agentTeam, companyName, agentTeamFilter) {
  const teamName = getAgentTeamName(agentTeam, agentTeamFilter);
  if (teamName && companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withTeam', {
      name,
      team: teamName,
      company: companyName
    });
  } else if (teamName && !companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withTeamNoCompany', {
      name,
      team: teamName
    });
  } else if (!teamName && companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withoutTeam', {
      name,
      company: companyName
    });
  } else {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withoutTeamNoCompany', {
      name
    });
  }
}
function formatAppTitle(name, categories, companyName, categoryFilter) {
  const categoryPart = formatCategoriesForTitle(categories, categoryFilter);
  if (categoryPart && companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.app.withCategories', {
      name,
      categories: categoryPart,
      company: companyName
    });
  } else if (categoryPart && !companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.app.withCategoriesNoCompany', {
      name,
      categories: categoryPart
    });
  } else if (!categoryPart && companyName) {
    return I18n.text('marketplaceStorefront.listingTitles.app.withoutCategories', {
      name,
      company: companyName
    });
  } else {
    return I18n.text('marketplaceStorefront.listingTitles.app.withoutCategoriesNoCompany', {
      name
    });
  }
}
function getAgentTeamName(agentTeam, agentTeamFilter) {
  if (!agentTeam || !(agentTeamFilter !== null && agentTeamFilter !== void 0 && agentTeamFilter.options)) {
    return undefined;
  }

  // Find the matching option from the filter config
  const teamOption = agentTeamFilter.options.find(option => option.value === agentTeam);
  return teamOption === null || teamOption === void 0 ? void 0 : teamOption.text;
}
function formatCategoriesForTitle(categories, categoryFilter) {
  if (!categories || categories.length === 0) {
    return undefined;
  }

  // Translate category enum values using filter config
  const translatedCategories = categories.map(category => {
    var _categoryFilter$optio;
    // Find the matching option from the filter config
    const categoryOption = categoryFilter === null || categoryFilter === void 0 || (_categoryFilter$optio = categoryFilter.options) === null || _categoryFilter$optio === void 0 ? void 0 : _categoryFilter$optio.find(option => option.value === category);
    return categoryOption === null || categoryOption === void 0 ? void 0 : categoryOption.text; // Remove fallback to prevent enum values
  }).filter(text => Boolean(text)); // Type guard to ensure string[]

  if (translatedCategories.length === 0) {
    return undefined;
  }
  if (translatedCategories.length === 1) {
    return translatedCategories[0];
  }
  return I18n.formatList(translatedCategories);
}